"use client";

import { useEffect, type ReactElement } from "react";
import dynamic from "next/dynamic";
import type { TeamsGalleryTeamOverlayProps } from "@/components/ui/Cteamsgallery";
import { useRegisterModal } from "@/contexts/layout";
import { useTeamModal, useTeamModalHostBridge } from "./team-modal-context";
import {
  isNewSubjectOpenState,
  TeamModalMode,
  TeamModalEditIntent,
  type TeamModalOpenExistingState,
  type TeamModalOpenState,
} from "./team-modal-types";

const TeamsGalleryTeamOverlay = dynamic<TeamsGalleryTeamOverlayProps>(
  () =>
    import("@/components/ui/Cteamsgallery").then(
      (module) => module.TeamsGalleryTeamOverlay,
    ),
  { ssr: false },
);

const TEAM_MODAL_STACK_ID = "team-modal";

const DOCUMENT_READY_STATE_COMPLETE = "complete";
const WINDOW_LOAD_EVENT = "load";
const TEAM_MODAL_CHUNK_PRELOAD_DELAY_MS = 1_500;

function preloadTeamOverlayChunk(): void {
  void import("@/components/ui/Cteamsgallery").catch(() => undefined);
}

/**
 * Idle-warm the overlay chunk so the first open from ANY screen morphs
 * immediately instead of stalling on a network fetch. The session screens
 * used to bundle the overlay eagerly (the team chip imported it statically)
 * and the landing already idle-preloads the same chunk — this keeps every
 * origin at that warmth now that the only importer is this host's dynamic().
 */
function useTeamOverlayChunkPreload(): void {
  useEffect(() => {
    let cancelScheduledPreload: VoidFunction | undefined;

    const scheduleChunkPreload = (): void => {
      if (typeof window.requestIdleCallback === "function") {
        let idleCallbackId: number | undefined;
        const timeoutId = window.setTimeout(() => {
          idleCallbackId = window.requestIdleCallback(preloadTeamOverlayChunk);
        }, TEAM_MODAL_CHUNK_PRELOAD_DELAY_MS);
        cancelScheduledPreload = () => {
          window.clearTimeout(timeoutId);
          if (idleCallbackId !== undefined) {
            window.cancelIdleCallback(idleCallbackId);
          }
        };
        return;
      }

      const timeoutId = window.setTimeout(
        preloadTeamOverlayChunk,
        TEAM_MODAL_CHUNK_PRELOAD_DELAY_MS,
      );
      cancelScheduledPreload = () => {
        window.clearTimeout(timeoutId);
      };
    };

    if (document.readyState === DOCUMENT_READY_STATE_COMPLETE) {
      scheduleChunkPreload();
    } else {
      window.addEventListener(WINDOW_LOAD_EVENT, scheduleChunkPreload, {
        once: true,
      });
    }

    return () => {
      window.removeEventListener(WINDOW_LOAD_EVENT, scheduleChunkPreload);
      cancelScheduledPreload?.();
    };
  }, []);
}

function initialEditIntentFor(
  openState: TeamModalOpenExistingState,
): TeamModalEditIntent | undefined {
  return openState.modeState.mode === TeamModalMode.Edit
    ? openState.modeState.editIntent
    : undefined;
}

/** The overlay's subject props per open state. A New subject mounts with no
 *  team id (`teamId: null` IS the overlay's creation marker) and edit invoked;
 *  after ANY in-place retarget's subject swap the id points at the team the
 *  overlay already retargeted itself onto, so the changed prop is a mirror
 *  catching up, never a fresh open. */
interface OverlaySubjectProps {
  teamId: string | null;
  initialMode: TeamModalMode;
  initialEditIntent: TeamModalEditIntent | undefined;
}

function overlaySubjectPropsFor(
  openState: TeamModalOpenState,
): OverlaySubjectProps {
  if (isNewSubjectOpenState(openState)) {
    return {
      teamId: null,
      initialMode: TeamModalMode.Edit,
      initialEditIntent: undefined,
    };
  }
  return {
    teamId: openState.subject.teamId,
    initialMode: openState.modeState.mode,
    initialEditIntent: initialEditIntentFor(openState),
  };
}

/**
 * The ONE mount of `TeamsGalleryTeamOverlay`. Mounted at the app root (inside
 * the gallery tweaks owner so the panel's design axes reach the modal from
 * every origin) and driven purely by the controller's state — the overlay
 * itself keeps its exact morph/collapse/scroll-lock behavior; only ownership
 * moved here.
 */
export function TeamModalHost(): ReactElement | null {
  const { close } = useTeamModal();
  const {
    openState,
    openGeneration,
    overlayHandleRef,
    onOverlayClosed,
    onPreflightActiveChange,
    onEditorActiveChange,
    onEditIntentFallback,
    onSubjectRetargeted,
  } = useTeamModalHostBridge();

  useTeamOverlayChunkPreload();

  // One stack entry for the whole modal, whatever its mode. No shared
  // backdrop — the overlay paints its own scrim. A stack-driven close (pop /
  // closeAll) routes the guarded close, exactly like Escape's outermost step.
  useRegisterModal({
    id: TEAM_MODAL_STACK_ID,
    visible: openState != null,
    onClose: close,
    backdrop: null,
  });

  if (openState == null) return null;

  // The overlay reads its initial-mode props at mount only, and it remounts
  // per open (the generation key). The live modeState can only diverge from
  // the entry mode through the overlay's own reports, which exist only after
  // that mount — so at every mount the live value IS the entry value. Every
  // mid-life subject-prop change is an in-place retarget the overlay just
  // reported, which it recognizes as its own (see overlaySubjectPropsFor).
  const subjectProps = overlaySubjectPropsFor(openState);
  return (
    <TeamsGalleryTeamOverlay
      key={openGeneration}
      visible
      teamId={subjectProps.teamId}
      morphSource={openState.origin.morphSource}
      aboveModals={openState.origin.aboveModals}
      initialMode={subjectProps.initialMode}
      initialEditIntent={subjectProps.initialEditIntent}
      onEditIntentFallback={onEditIntentFallback}
      onClose={onOverlayClosed}
      onPreflightActiveChange={onPreflightActiveChange}
      onEditorActiveChange={onEditorActiveChange}
      onSubjectRetargeted={onSubjectRetargeted}
      handleRef={overlayHandleRef}
    />
  );
}
